import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useQaza } from '../../contexts/QazaContext';
import { useAuth } from '../../contexts/AuthContext';
import { AlertTriangle, CheckCircle2, RefreshCw, LogIn, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';

const QazaSection = ({ onLogin, onMadeUp }) => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL, language } = useTheme();
  const { isAuthenticated } = useAuth();
  const { pending, madeUp, loading } = useQaza();
  const [showMadeUp, setShowMadeUp] = useState(false);
  const [makingUp, setMakingUp] = useState(null);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  };

  const handleMakeUp = async (logId) => {
    setMakingUp(logId);
    if (onMadeUp) {
      await onMadeUp(logId);
    }
    setMakingUp(null);
  };

  return (
    <div
      data-testid="qaza-section"
      className={`rounded-2xl overflow-hidden ${isDarkMode ? 'glass-card' : 'bg-white shadow-sm border border-gray-100'}`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div className={`px-4 py-3 flex items-center justify-between border-b ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
        <div className="flex items-center gap-2">
          <AlertTriangle className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`} />
          <h2 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('qaza.title')}
          </h2>
          {pending.length > 0 && (
            <span className="ml-1 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-500 text-white">
              {pending.length}
            </span>
          )}
        </div>
        {loading && <RefreshCw className={`w-3.5 h-3.5 animate-spin ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />}
      </div>

      <div className="p-4 space-y-3">
        {/* Not logged in */}
        {!isAuthenticated ? (
          <div className="flex flex-col items-center py-4 gap-3">
            <p className={`text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('qaza.loginRequired')}
            </p>
            {onLogin && (
              <Button onClick={onLogin} size="sm" className="bg-[#1a365d] hover:bg-[#2d5a87] text-white gap-2">
                <LogIn className="w-3.5 h-3.5" />
                {t('settings.signInGoogle')}
              </Button>
            )}
          </div>
        ) : pending.length === 0 ? (
          <div className="flex items-center gap-3 py-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
              <CheckCircle2 className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('qaza.noMissed')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map((log) => (
              <div
                key={log.log_id}
                data-testid={`qaza-item-${log.log_id}`}
                className={`flex items-center justify-between p-3 rounded-xl ${
                  isDarkMode ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200'
                }`}
              >
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                    <AlertTriangle className={`w-4 h-4 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                  </div>
                  <div className={isRTL ? 'text-right' : ''}>
                    <p className={`font-semibold text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                      {t(`prayers.${log.prayer}`, log.prayer)}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-amber-400/60' : 'text-amber-600/70'}`}>
                      {t('qaza.missedOn')} {formatDate(log.original_date)}
                    </p>
                  </div>
                </div>
                <button
                  data-testid={`makeup-btn-${log.log_id}`}
                  onClick={() => handleMakeUp(log.log_id)}
                  disabled={makingUp === log.log_id}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    isDarkMode
                      ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30'
                      : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-300'
                  } disabled:opacity-50`}
                >
                  {makingUp === log.log_id
                    ? <RefreshCw className="w-3 h-3 animate-spin" />
                    : <CheckCircle2 className="w-3 h-3" />
                  }
                  <span>{t('qaza.markMadeUp')}</span>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Made-up collapsible */}
        {isAuthenticated && madeUp.length > 0 && (
          <div>
            <button
              data-testid="toggle-madeup"
              onClick={() => setShowMadeUp(prev => !prev)}
              className={`w-full flex items-center justify-between py-2 text-xs font-medium transition-colors ${
                isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'
              }`}
            >
              <span>{t('qaza.madeUp')} ({madeUp.length})</span>
              {showMadeUp ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>

            {showMadeUp && (
              <div className="space-y-2 mt-1">
                {madeUp.map((log) => (
                  <div
                    key={log.log_id}
                    className={`flex items-center justify-between p-3 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}
                  >
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                        <CheckCircle2 className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                      </div>
                      <div className={isRTL ? 'text-right' : ''}>
                        <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {t(`prayers.${log.prayer}`, log.prayer)}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`}>
                          {t('qaza.missedOn')} {formatDate(log.original_date)}
                          {log.made_up_at && <> · {t('qaza.madeUpOn')} {formatDate(log.made_up_at.slice(0, 10))}</>}
                        </p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
                      {t('qaza.madeUpLabel')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QazaSection;
